import { get, groupBy } from 'lodash';
import i18n from 'i18n';
import {
  ALL_SELECT_VALUE,
  AUTO_UPDATE_VALUE
} from 'core/limitation/ProductGroup';
import {
  deviceCharacteristicInfoExtraGetter,
  storeInfoExtraGetter
} from 'core/adRequestSource/AdRequestSourceManager';

export {
  getIsDeviceUnit,
  getIsStoreUnit,
  getOutdoorDeliveryUnit
} from 'core/channelGroup/ChannelGroupSettings';

/**
 * Group outdoor `spaces` (whose `extra` starts with `"${municipality}/..."` —
 * the rest of the segments are caller-defined leaves: `storeNum` for store
 * delivery, `storeNum/locationName/deviceCharacteristic` for device delivery)
 * into a municipality → leaves hierarchy. Labels are resolved from the
 * provided `labelMap` (built from the freshly-fetched outdoor attribute bag,
 * whose top-level entries are post-fixed server-side per locale); falls back
 * to the value itself when missing.
 */
export const toOutdoorHierarchyOptions = (
  options: SelectOptions[],
  labelMap?: Map<string | number, string>
): SelectOptions[] => {
  const validOptions = options.filter((opt) => {
    const extra = get(opt, 'extra', '') as string;
    return extra && extra.includes('/');
  });
  const spaceGroupByLeaf = groupBy(validOptions, 'extra');
  const leafEntries = Object.values(spaceGroupByLeaf).map(
    (spacesOfLeaf: SelectOptions[]) => {
      const value = get(spacesOfLeaf[0], 'extra', '') as string;
      return { label: labelMap?.get(value) ?? value, value };
    }
  );
  // Group leaves by municipality (first segment of `extra`), build hierarchy
  const leavesByMunicipality = groupBy(leafEntries, (leaf) => {
    const val = leaf.value.toString();
    const idx = val.indexOf('/');
    return idx >= 0 ? val.substring(0, idx) : val;
  });
  return Object.entries(leavesByMunicipality).map(([municipality, leaves]) => ({
    label: labelMap?.get(municipality) ?? municipality,
    value: municipality,
    options: leaves.map((leaf) => ({
      label: leaf.label,
      value: leaf.value
    }))
  }));
};

/**
 * Pick the matching extra getter for outdoor space records based on whether
 * the channel delivers by store or by device.
 */
export const getOutdoorExtraGetter = (isStoreUnit: boolean) =>
  isStoreUnit ? storeInfoExtraGetter : deviceCharacteristicInfoExtraGetter;

/**
 * i18n key mappings for store / device / space variants. Section title
 * (`select${GroupType}Section`) is asymmetric (interpolates groupType for the
 * space variant) and stays inline at the callsite.
 */
export const getDeliveryUnitI18nKeys = (
  isStoreUnit: boolean,
  isDeviceUnit: boolean
) => {
  if (isStoreUnit) {
    return {
      selectAll: 'productGroupForm.labels.selectAllStore',
      condition: 'productGroupForm.labels.conditionStore',
      conditionHint: 'productGroupForm.hints.conditionStore',
      filterButton: 'productGroupForm.buttons.filterStore'
    };
  }
  if (isDeviceUnit) {
    return {
      selectAll: 'productGroupForm.labels.selectAllDevice',
      condition: 'productGroupForm.labels.conditionDevice',
      conditionHint: 'productGroupForm.hints.conditionDevice',
      filterButton: 'productGroupForm.buttons.filterDevice'
    };
  }
  return {
    selectAll: 'productGroupForm.labels.selectAllSpace',
    condition: 'productGroupForm.labels.conditionSpace',
    conditionHint: 'productGroupForm.hints.conditionSpace',
    filterButton: 'productGroupForm.buttons.filterSpace'
  };
};

/**
 * Prefix the `selectAll` / `condition` virtual options and append the
 * outdoor-space hierarchy (store-tree or device-tree, picked by
 * `isStoreUnit`). Caller supplies the label map matching the chosen
 * hierarchy.
 */
export const buildOutdoorDeliveryTargetOptions = (
  spaces: SelectOptions[],
  options: {
    isStoreUnit: boolean;
    isDeviceUnit: boolean;
    labelMap?: Map<string | number, string>;
  }
): SelectOptions[] => {
  const { isStoreUnit, isDeviceUnit, labelMap } = options;
  const keys = getDeliveryUnitI18nKeys(isStoreUnit, isDeviceUnit);
  return [
    { label: i18n.t(keys.selectAll), value: ALL_SELECT_VALUE },
    { label: i18n.t(keys.condition), value: AUTO_UPDATE_VALUE },
    ...toOutdoorHierarchyOptions(spaces, labelMap)
  ];
};
